import { createTheme } from '@mui/material/styles';
import { getPalette } from './palette';

// Soft, layered shadow scale (replaces MUI's heavier defaults) for a lighter, modern feel.
const softShadow = (mode) => {
  const c = mode === 'light' ? '145,158,171' : '0,0,0';
  const a = mode === 'light' ? [0.08, 0.12, 0.16] : [0.4, 0.5, 0.6];
  return [
    'none',
    `0 1px 2px 0 rgba(${c},${a[0]})`,
    `0 2px 8px -2px rgba(${c},${a[0]})`,
    `0 4px 16px -4px rgba(${c},${a[1]})`,
    `0 8px 24px -6px rgba(${c},${a[1]})`,
    `0 12px 32px -8px rgba(${c},${a[2]})`,
  ];
};

/** Build the MUI theme for a colour mode. Consumed by main.jsx. */
export const buildTheme = (mode = 'light') => {
  const palette = getPalette(mode);
  const shadows = softShadow(mode);
  // MUI needs exactly 25 shadow slots; extend our soft scale to fill the rest.
  const shadowScale = Array.from({ length: 25 }, (_, i) => shadows[Math.min(i, shadows.length - 1)]);

  return createTheme({
    palette,
    shape: { borderRadius: 12 },
    shadows: shadowScale,
    typography: {
      fontFamily: ['Inter', 'system-ui', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      h1: { fontFamily: 'Sora, Inter, sans-serif', fontWeight: 800, letterSpacing: '-0.02em' },
      h2: { fontFamily: 'Sora, Inter, sans-serif', fontWeight: 800, letterSpacing: '-0.02em' },
      h3: { fontFamily: 'Sora, Inter, sans-serif', fontWeight: 700, letterSpacing: '-0.015em' },
      h4: { fontFamily: 'Sora, Inter, sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
      h5: { fontWeight: 700, letterSpacing: '-0.01em' },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
      overline: { fontWeight: 700, letterSpacing: '0.08em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: palette.mode === 'light' ? 'rgba(148,163,184,0.5)' : 'rgba(148,163,184,0.35)',
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: palette.mode === 'light' ? 'rgba(100,116,139,0.7)' : 'rgba(148,163,184,0.55)',
          },
          'html, body, #root': { height: '100%' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, paddingInline: 16 },
          sizeLarge: { paddingBlock: 10 },
          containedPrimary: {
            boxShadow: '0 6px 16px -6px rgba(79,70,229,0.5)',
            '&:hover': { boxShadow: '0 8px 20px -6px rgba(79,70,229,0.6)' },
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${palette.divider}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: 'none' }, rounded: { borderRadius: 16 } },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: {
            backdropFilter: 'blur(10px)',
            backgroundColor: palette.mode === 'light' ? 'rgba(255,255,255,0.8)' : 'rgba(17,26,46,0.8)',
            borderBottom: `1px solid ${palette.divider}`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 8 },
          sizeSmall: { height: 22 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: palette.divider },
          head: { fontWeight: 700, color: palette.text.secondary, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: 8, fontSize: '0.75rem', fontWeight: 500, paddingBlock: 6, paddingInline: 10 },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiListItemButton: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiMenu: { styleOverrides: { paper: { borderRadius: 12, border: `1px solid ${palette.divider}`, marginTop: 4 } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 18 } } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 999, height: 6 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 12 } } },
    },
  });
};

export default buildTheme;
